import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DataTable, { Column, DataTableFetchParams } from '../../components/ui/DataTable';
import {
  backgroundTasksService,
  YACHT_WORLD_IMPORT_TASK_TYPE,
} from '../../services/background-task.service';
import { BackgroundTask } from '../../interfaces/background-task.interface';

const STATUS_COLORS: Record<string, string> = {
  queued: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
  running: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200',
  completed: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200',
  failed: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
  cancelled: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200',
};

const IN_FLIGHT = new Set(['queued', 'running']);
/** Only polls while something is actually running, so an idle tab is silent. */
const POLL_MS = 5000;

const formatTime = (value: string | null) =>
  value ? new Date(value).toLocaleString() : '—';

const ImportTasksTable = () => {
  const { t } = useTranslation('common');
  const [refreshKey, setRefreshKey] = useState(0);
  const [polling, setPolling] = useState(false);

  const handleFetch = useCallback(async (params: DataTableFetchParams) => {
    const response = await backgroundTasksService.list({
      type: YACHT_WORLD_IMPORT_TASK_TYPE,
      page: params.page,
      take: params.pageSize,
      order: 'DESC',
    });
    setPolling(response.data.some((task) => IN_FLIGHT.has(task.status)));
    return {
      data: response.data,
      total: response.meta.itemCount,
      totalPages: response.meta.pageCount,
    };
  }, []);

  useEffect(() => {
    if (!polling) return;
    const id = setInterval(() => setRefreshKey((k) => k + 1), POLL_MS);
    return () => clearInterval(id);
  }, [polling]);

  const columns: Column<BackgroundTask>[] = [
    {
      key: 'file',
      header: t('yw_task_file'),
      render: (task) => (
        <div>
          <div className="font-medium">{task.input?.fileName ?? '—'}</div>
          <div className="text-xs text-gray-500">
            {t('yw_task_records', { n: task.input?.recordCount ?? 0 })}
          </div>
        </div>
      ),
    },
    {
      key: 'status',
      header: t('status'),
      render: (task) => (
        <div className="space-y-1">
          <span
            className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${STATUS_COLORS[task.status] ?? ''}`}
          >
            {task.status}
          </span>
          {IN_FLIGHT.has(task.status) && (
            <div className="flex items-center gap-1">
              <div className="w-16 bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                <div className="bg-blue-500 h-1.5 rounded-full" style={{ width: `${task.progress}%` }} />
              </div>
              <span className="text-xs text-gray-500">{task.progress}%</span>
            </div>
          )}
          {task.errorMessage && (
            <div className="text-xs text-red-500">{task.errorMessage}</div>
          )}
        </div>
      ),
    },
    {
      key: 'outcome',
      header: t('yw_task_outcome'),
      render: (task) => {
        const result = task.result;
        if (!result) return <span className="text-gray-400 text-sm">—</span>;
        return (
          <div className="text-xs space-y-0.5">
            <div>
              {t('yw_task_created', { n: result.created.length })} ·{' '}
              {t('yw_task_updated', { n: result.updated.length })}
            </div>
            {result.skipped.length > 0 && (
              // The reasons are the whole point of the column — a hover beats a
              // second screen nobody opens.
              <div
                className="text-yellow-600 dark:text-yellow-400"
                title={result.skipped.map((s) => `${s.documentId}: ${s.reason}`).join('\n')}
              >
                {t('yw_task_skipped', { n: result.skipped.length })}
              </div>
            )}
            {result.imageFailures.length > 0 && (
              <div
                className="text-red-500"
                title={result.imageFailures
                  .map((f) => `${f.documentId}: ${f.reason} — ${f.url}`)
                  .join('\n')}
              >
                {t('yw_task_photo_failures', { n: result.imageFailures.length })}
              </div>
            )}
            <div className="text-gray-500">
              {t('yw_task_points_spent', { points: result.pointsSpent })}
            </div>
          </div>
        );
      },
    },
    {
      key: 'startedAt',
      header: t('yw_task_started'),
      render: (task) => <span className="text-sm">{formatTime(task.startedAt)}</span>,
    },
    {
      key: 'completedAt',
      header: t('yw_task_finished'),
      render: (task) => <span className="text-sm">{formatTime(task.completedAt)}</span>,
    },
  ];

  return (
    <DataTable
      className="flex-1 min-h-0"
      columns={columns}
      onFetch={handleFetch}
      initialState={{ pageSize: 20 }}
      refreshKey={refreshKey}
      emptyMessage={t('yw_tasks_empty')}
    />
  );
};

export default ImportTasksTable;
